import { assetPaths } from "../assetPaths";
import { ExpertiseEnum } from "../expertiseEnum";
import { getMessages } from "../i18n/messages";
import type { Locale } from "../i18n/locales";
import { TeamMemberEnum } from "../teamData";
import { Judgement } from "./expertiseCityStrings";
import type { Messages } from "@/messages/buildCatalog";
import { hydrateExpertiseSolutions } from "./expertiseSolutions/hydrateExpertiseSolutions";
import { orderGesellschaftsrechtSolutionsForLocale } from "./expertiseSolutions/orderGesellschaftsrechtSolutions";
import type { Solution } from "./expertiseSolutions/types";
import { getFeaturedAwardsForExpertise } from "../featuredAwards";
import type { FeaturedAward } from "../featuredAwards";

export interface ExpertiseStringSet {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  featuredAwards?: FeaturedAward[];
  solutions?: Record<string, Solution>;
  teamMembers?: TeamMemberEnum[];
  urteile?: Judgement[];
}

type BghUrteileKey = keyof NonNullable<Messages["bghUrteile"]>;

const bghUrteileKeyByExpertise: Partial<Record<ExpertiseEnum, BghUrteileKey>> =
  {
    [ExpertiseEnum.Gesellschaftsrecht]: "bghZr2",
    [ExpertiseEnum.Erbrecht]: "bghZr4Erbrecht",
    [ExpertiseEnum.Insolvenzrecht]: "bghZr9Insolvenzrecht",
  };

function bghUrteileForExpertise(
  locale: Locale,
  expertise: ExpertiseEnum,
): Judgement[] | undefined {
  const pack = getMessages(locale).bghUrteile;
  if (!pack) return undefined;
  const key = bghUrteileKeyByExpertise[expertise];
  if (!key) return undefined;
  const list = pack[key];
  return list?.length ? (list as Judgement[]) : undefined;
}

type ExpertiseCoreEntry = Pick<ExpertiseStringSet, "image" | "teamMembers">;

/** Non-translatable wiring (assets, team). BGH hub judgments live in locale bghUrteile.json (see buildCatalog). */
const expertiseCore: Record<ExpertiseEnum, ExpertiseCoreEntry> = {
  [ExpertiseEnum.Arbeitsrecht]: {
    image: assetPaths.images.expertise.arbeitsrecht,
    teamMembers: [TeamMemberEnum.MoritzRiehl],
  },
  [ExpertiseEnum.Erbrecht]: {
    image: assetPaths.images.expertise.erbrecht,
    teamMembers: [TeamMemberEnum.MoritzRiehl],
  },
  [ExpertiseEnum.Gesellschaftsrecht]: {
    image: assetPaths.images.expertise.gesellschaftsrecht,
    teamMembers: [
      TeamMemberEnum.HubertusScherbarth,
      TeamMemberEnum.MoritzRiehl,
    ],
  },
  [ExpertiseEnum.Startuprecht]: {
    image: assetPaths.images.expertise.startuprecht,
    teamMembers: [
      TeamMemberEnum.HubertusScherbarth,
      TeamMemberEnum.MoritzRiehl,
    ],
  },
  [ExpertiseEnum.Steuerrecht]: {
    image: assetPaths.images.expertise.steuerrecht,
    teamMembers: [TeamMemberEnum.HubertusScherbarth],
  },
  [ExpertiseEnum.Prozessfuehrung]: {
    image: assetPaths.images.expertise.prozessfuehrung,
    teamMembers: [
      TeamMemberEnum.HubertusScherbarth,
      TeamMemberEnum.MoritzRiehl,
    ],
  },
  [ExpertiseEnum.InternetMedienRecht]: {
    image: assetPaths.images.expertise.internetmedienrecht,
    teamMembers: [
      TeamMemberEnum.HubertusScherbarth,
      TeamMemberEnum.MoritzRiehl,
    ],
  },
  [ExpertiseEnum.Insolvenzrecht]: {
    image: assetPaths.images.expertise.insolvenzrecht,
    teamMembers: [TeamMemberEnum.HubertusScherbarth],
  },
};

function hubTexts(locale: Locale) {
  return getMessages(locale).expertise.hub;
}

export function getExpertiseStrings(
  locale: Locale,
): Record<ExpertiseEnum, ExpertiseStringSet> {
  const hub = hubTexts(locale);
  const es = getMessages(locale).expertiseSolutions;
  const arbeitsrechtSol = hydrateExpertiseSolutions(es.arbeitsrecht);
  const erbrechtSol = hydrateExpertiseSolutions(es.erbrecht);
  const gesellschaftsrechtSol = orderGesellschaftsrechtSolutionsForLocale(
    hydrateExpertiseSolutions(es.gesellschaftsrecht),
    locale,
  );
  const startupSol = hydrateExpertiseSolutions(es.startuprecht);
  const internetSol = hydrateExpertiseSolutions(es.internetMedienrecht);
  return {
    [ExpertiseEnum.Arbeitsrecht]: {
      title: hub.arbeitsrecht.title,
      description: hub.arbeitsrecht.description,
      imageAlt: hub.arbeitsrecht.imageAlt,
      ...expertiseCore[ExpertiseEnum.Arbeitsrecht],
      solutions: arbeitsrechtSol,
    },
    [ExpertiseEnum.Erbrecht]: {
      title: hub.erbrecht.title,
      description: hub.erbrecht.description,
      imageAlt: hub.erbrecht.imageAlt,
      ...expertiseCore[ExpertiseEnum.Erbrecht],
      solutions: erbrechtSol,
      urteile: bghUrteileForExpertise(locale, ExpertiseEnum.Erbrecht),
    },
    [ExpertiseEnum.Gesellschaftsrecht]: {
      title: hub.gesellschaftsrecht.title,
      description: hub.gesellschaftsrecht.description,
      imageAlt: hub.gesellschaftsrecht.imageAlt,
      featuredAwards: getFeaturedAwardsForExpertise(
        ExpertiseEnum.Gesellschaftsrecht,
        locale,
      ),
      ...expertiseCore[ExpertiseEnum.Gesellschaftsrecht],
      solutions: gesellschaftsrechtSol,
      urteile: bghUrteileForExpertise(locale, ExpertiseEnum.Gesellschaftsrecht),
    },
    [ExpertiseEnum.Startuprecht]: {
      title: hub.startuprecht.title,
      description: hub.startuprecht.description,
      imageAlt: hub.startuprecht.imageAlt,
      featuredAwards: getFeaturedAwardsForExpertise(
        ExpertiseEnum.Startuprecht,
        locale,
      ),
      ...expertiseCore[ExpertiseEnum.Startuprecht],
      solutions: startupSol,
    },
    [ExpertiseEnum.Steuerrecht]: {
      title: hub.steuerrecht.title,
      description: hub.steuerrecht.description,
      imageAlt: hub.steuerrecht.imageAlt,
      featuredAwards: getFeaturedAwardsForExpertise(
        ExpertiseEnum.Steuerrecht,
        locale,
      ),
      ...expertiseCore[ExpertiseEnum.Steuerrecht],
    },
    [ExpertiseEnum.Prozessfuehrung]: {
      title: hub.prozessfuehrung.title,
      description: hub.prozessfuehrung.description,
      imageAlt: hub.prozessfuehrung.imageAlt,
      ...expertiseCore[ExpertiseEnum.Prozessfuehrung],
    },
    [ExpertiseEnum.InternetMedienRecht]: {
      title: hub.internetmedienrecht.title,
      description: hub.internetmedienrecht.description,
      imageAlt: hub.internetmedienrecht.imageAlt,
      ...expertiseCore[ExpertiseEnum.InternetMedienRecht],
      solutions: internetSol,
    },
    [ExpertiseEnum.Insolvenzrecht]: {
      title: hub.insolvenzrecht.title,
      description: hub.insolvenzrecht.description,
      imageAlt: hub.insolvenzrecht.imageAlt,
      featuredAwards: getFeaturedAwardsForExpertise(
        ExpertiseEnum.Insolvenzrecht,
        locale,
      ),
      ...expertiseCore[ExpertiseEnum.Insolvenzrecht],
      urteile: bghUrteileForExpertise(locale, ExpertiseEnum.Insolvenzrecht),
    },
  };
}

/** @deprecated Prefer `getExpertiseStrings(locale)` where locale is known. */
export const expertiseStrings: Record<ExpertiseEnum, ExpertiseStringSet> =
  getExpertiseStrings("de");
